// Aba "Arquivos" do módulo Campanhas (/files) — mesma anatomia da aba Listas.
// A contagem de arquivos vem de `filesCount`, uma subquery de COUNT em
// FileServices/ListService (não um include das options, que com `limit`
// duplicaria linhas e quebraria a paginação). A coluna secundária usa
// `message`. Quando a contagem não vier, a sublinha é omitida em vez de
// mostrar "0" — ver filesCountLabel em ContactLists/contactListRules.
import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";

import MainContainer from "../../components/MainContainer";
import CampaignsNav from "../../components/CampaignsNav";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import FileModal from "../../components/FileModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import { filesCountLabel } from "../ContactLists/contactListRules";

const TABLE_GRID = "2.4fr 2fr 110px";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const FileLists = () => {
    const c = useCampaignPageStyles({ grid: TABLE_GRID });

    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
        };
    }, [socket]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value);
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    const stop = (event, fn) => {
        event.stopPropagation();
        fn();
    };

    const renderEmpty = () => (
        <div className={c.emptyState}>
            <div className={c.emptyIcon}>
                <FolderOpenIcon />
            </div>
            <div className={c.emptyTitle}>
                {searchParam
                    ? i18n.t("files.empty.noResults")
                    : i18n.t("files.empty.title")}
            </div>
            {!searchParam && (
                <div className={c.emptyText}>{i18n.t("files.empty.text")}</div>
            )}
        </div>
    );

    if (user.profile === "user") {
        return (
            <MainContainer>
                <ForbiddenPage />
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <AerosConfirmModal
                open={Boolean(deletingFileList)}
                onClose={() => setDeletingFileList(null)}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
                icon={<DeleteOutlineIcon />}
                tone="danger"
                title={i18n.t("files.confirmDelete.title", {
                    name: deletingFileList?.name || "",
                })}
                description={i18n.t("files.confirmDelete.text")}
                confirmLabel={i18n.t("files.confirmDelete.confirm")}
            />
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            <div className={c.pageHeader}>
                <Typography component="div" className={c.pageTitle}>
                    {i18n.t("files.title")}{" "}
                    <span className={c.pageTitleCount}>· {files.length}</span>
                </Typography>
                <div className={c.headerActions}>
                    <InputBase
                        className={c.searchPill}
                        placeholder={i18n.t("files.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        startAdornment={<SearchIcon />}
                        inputProps={{ "aria-label": i18n.t("files.searchPlaceholder") }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        className={c.gradientButton}
                        startIcon={<AddIcon />}
                        onClick={handleOpenFileListModal}
                    >
                        {i18n.t("files.buttons.add")}
                    </Button>
                </div>
            </div>

            <div className={c.pageBody} onScroll={handleScroll}>
                <CampaignsNav />
                <div className={c.tableCard}>
                    <div className={c.tableHeadRow}>
                        <div className={c.tableHeadCell} style={{ paddingLeft: 20 }}>
                            {i18n.t("files.table.name")}
                        </div>
                        <div className={c.tableHeadCell}>
                            {i18n.t("fileModal.form.message")}
                        </div>
                        <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
                            {i18n.t("files.table.actions")}
                        </div>
                    </div>

                    {files.map((fileList) => {
                        const count = filesCountLabel(fileList.filesCount);
                        return (
                        <div
                            key={fileList.id}
                            className={`${c.tableRow} ${c.tableRowClickable}`}
                            onClick={() => handleEditFileList(fileList)}
                        >
                            <div className={c.nameCell}>
                                <div className={`${c.nameTile} ${c.nameTileCyan}`}>
                                    <FolderOpenIcon />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div className={c.nameText}>{fileList.name}</div>
                                    {count && <div className={c.nameSub}>{count}</div>}
                                </div>
                            </div>
                            <div
                                className={`${c.tableCell} ${c.cellMuted} ${c.cellEllipsis}`}
                                title={fileList.message || ""}
                            >
                                {fileList.message || "—"}
                            </div>
                            <div className={`${c.tableCell} ${c.cellActions}`}>
                                <Tooltip title={i18n.t("files.actions.edit")}>
                                    <IconButton
                                        className={c.rowActionButton}
                                        aria-label={i18n.t("files.actions.edit")}
                                        onClick={(e) => stop(e, () => handleEditFileList(fileList))}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={i18n.t("files.actions.delete")}>
                                    <IconButton
                                        className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                                        aria-label={i18n.t("files.actions.delete")}
                                        onClick={(e) => stop(e, () => setDeletingFileList(fileList))}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                        );
                    })}

                    {loading && (
                        <div className={c.loadingRow}>
                            <CircularProgress size={22} />
                        </div>
                    )}
                    {!loading && files.length === 0 && renderEmpty()}
                </div>
            </div>
        </MainContainer>
    );
};

export default FileLists;
